
'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { skipImageOptimizer } from '@/lib/image-optimizer';
import { toEmbedUrl } from '@/lib/video-embed';
import { EMBED_SANDBOX, isAllowedEmbedUrl } from '@/lib/sanitize-html';
import { PlaceholderMedia } from '../ui/placeholder-media';

interface FullscreenMediaProps {
  /**
   * The gallery, in order. Typed as media rather than a list of srcs because
   * the gallery can hold VIDEO items: handed bare strings, this component ran
   * every one of them through next/image, which meant a seller who added a
   * video got a broken viewer on a URL that is not an image and is not in
   * `remotePatterns` either.
   */
  media: ReadonlyArray<{ type?: 'image' | 'video'; src: string }>;
  startIndex: number;
  onClose: () => void;
}

export default function FullscreenMedia({ media, startIndex, onClose }: FullscreenMediaProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % media.length);
  }, [media.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  }, [media.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [nextImage, prevImage, onClose]);

  const current = media[currentIndex];
  // Resolved once per render rather than inside the branch below, so the
  // fallback and the frame cannot disagree about whether the URL is usable.
  // Held to the sanitizer's allowlist as well as the normaliser, matching the
  // gallery frame on the product page. See isAllowedEmbedUrl.
  const rawEmbed = current?.type === 'video' ? toEmbedUrl(current.src) : null;
  const embed = rawEmbed && isAllowedEmbedUrl(rawEmbed) ? rawEmbed : null;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-black/80 backdrop-blur-sm border-none p-0 w-screen h-screen max-h-none max-w-none overflow-hidden rounded-none flex items-center justify-center">
        <DialogHeader className="sr-only">
          <DialogTitle>Product gallery</DialogTitle>
          <DialogDescription>A fullscreen view of the product gallery. Use arrow keys to navigate.</DialogDescription>
        </DialogHeader>

        <DialogClose asChild>
            <Button
              variant="outline"
              size="icon"
              className="absolute top-4 right-4 z-50 h-12 w-12 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
              aria-label="Close fullscreen viewer"
            >
              <X className="h-8 w-8" aria-hidden />
            </Button>
        </DialogClose>

        {media.length > 1 && (
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 h-12 w-12 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-8 w-8" aria-hidden />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 h-12 w-12 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
              aria-label="Next image"
            >
              <ChevronRight className="h-8 w-8" aria-hidden />
            </Button>
          </>
        )}

        <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
          {!current ? (
            <PlaceholderMedia />
          ) : current.type === 'video' ? (
            /* Same hardening as the gallery frame on the product page: the
               normaliser decides the host, so an unparseable src shows a
               placeholder instead of a frame the CSP would block anyway. */
            embed ? (
              <iframe
                className="w-full h-full"
                src={embed}
                title={`Fullscreen video ${currentIndex + 1}`}
                sandbox={EMBED_SANDBOX}
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            ) : (
              <PlaceholderMedia />
            )
          ) : (
            <Image
              src={current.src}
              alt={`Fullscreen image ${currentIndex + 1}`}
              fill
              sizes="100vw"
              unoptimized={skipImageOptimizer(current.src)}
              style={{ objectFit: 'contain' }}
              className="select-none"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
